import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const LawnCare = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my lawn care business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps lawn care businesses capture more leads, automate follow-ups, reduce no-shows, and grow faster. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for lawn care businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most lawn care businesses see ROI within the first month."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy business owners with zero tech experience. Setup takes 5 minutes with UK-based support."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my lawn care business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes is specifically designed for local service businesses like lawn care. Features include appointment scheduling, automated reminders, lead capture, and customer management."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Lawn Care - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Lawn Care CRM", url: "/industries/lawn-care" }
  ];

  const industryData = {
    name: 'Lawn Care & Landscaping',
    icon: '🌱',
    title: 'Fill Your Schedule & Build Recurring Lawn Care Contracts',
    subtitle: 'Automated seasonal reminders, online quotes, and maintenance plans that keep your crews busy year-round',
    
    challenges: [
      'Seasonal business means scrambling for work every spring',
      'One-time customers not coming back for regular maintenance',
      'Spending hours creating quotes for basic lawn care jobs',
      'Weather delays causing scheduling chaos and customer complaints',
      'Hard to get customers to commit to weekly or monthly service plans',
      'Missing calls during the day when you\'re mowing or on job sites',
      'Customers forgetting about spring clean-ups and fall leaf removal',
      'Manual invoicing taking hours at the end of each week'
    ],
    
    solutions: [
      'Seasonal reminder campaigns automatically bring customers back for spring, summer, fall services',
      'Online instant quote calculator for basic lawn care, edging, and mowing services',
      'Automated monthly/weekly service plan promotions convert one-time jobs into recurring contracts',
      'Weather update SMS keeps customers informed about delays without manual calls',
      'Missed call text-back captures enquiries even when you\'re operating loud equipment',
      'Online booking for quotes and initial consultations fills your pre-season schedule',
      'Before/after photo automation showcases your work and generates referrals',
      'Automated invoicing and payment reminders reduce collection time by 60%'
    ],
    
    features: [
      'Seasonal Campaign Automation (Spring/Summer/Fall)',
      'Instant Lawn Care Quote Calculator',
      'Online Booking for Quotes & Consultations',
      'Weekly/Monthly Service Plan Promotion Campaigns',
      'Weather Update & Delay SMS Notifications',
      'Missed Call Text-Back System',
      'Before/After Photo Gallery with Auto-Sharing',
      'Recurring Service Contract Management',
      'Automated Appointment Reminders',
      'Google Review Request Automation',
      'Automated Invoicing & Payment Tracking',
      'Referral Request Campaigns'
    ],
    
    results: [
      '60% of customers on recurring plans',
      'Book spring schedule by mid-February',
      '45% increase in year-round revenue',
      'Fill schedule 2 weeks in advance',
      'Reduce admin time by 12 hours/week',
      'Average 4.8★ Google rating'
    ],
    
    reviews: [
      {
        author: 'Tom Henderson',
        location: 'Leeds',
        text: 'The seasonal reminder campaigns have transformed my business. I used to scramble for work every spring. Now customers automatically get reminders in February and book their spring clean-ups before I even start advertising. My spring schedule is full by mid-March.',
        result: 'Spring schedule fully booked by mid-March'
      },
      {
        author: 'Mark Davies',
        location: 'Birmingham',
        text: 'The recurring service plan campaigns are brilliant. 60% of my customers are now on weekly or bi-weekly mowing plans. That\'s consistent work throughout the season instead of one-off jobs. Revenue is up 45% and I can plan crew schedules properly.',
        result: '60% on recurring plans, 45% revenue increase'
      },
      {
        author: 'Paul Roberts',
        location: 'Manchester',
        text: 'The instant quote calculator has saved me countless hours. Customers get preliminary pricing for basic lawn care immediately. My conversion rate has doubled because I\'m not losing leads who need pricing right away. I only do in-person quotes for complex landscaping now.',
        result: 'Quote conversion doubled'
      },
      {
        author: 'Steve Wilson',
        location: 'Bristol',
        text: 'The missed call text-back is perfect for lawn care. I\'m on a mower 8 hours a day and can\'t answer my phone. Now every missed call gets an instant text and 70% of them text me back with their needs. I\'m not losing business to competitors anymore.',
        result: '70% of missed calls convert via text'
      },
      {
        author: 'James Mitchell',
        location: 'Liverpool',
        text: 'The before/after photo automation has been amazing for marketing. The system automatically requests photos after jobs and showcases them on my website. New customers constantly mention seeing the photos. Referrals are up 50% because customers share the photos with neighbors.',
        result: '50% increase in referrals from photo sharing'
      },
      {
        author: 'David Foster',
        location: 'Glasgow',
        text: 'The automated invoicing has saved me 12 hours per week. I used to spend every Friday night doing paperwork. Now invoices go out automatically after each job and payment reminders chase up late payers. I get paid 60% faster and have my evenings back.',
        result: 'Saves 12 hours weekly, paid 60% faster'
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Lawn Care UK | Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for lawn care businesses. Capture more leads, automate follow-up, grow faster. Start £5 trial today."
        keywords="CRM for lawn care UK, lawn care business software, lawn care customer management, lawn care lead generation UK"
        url="/industries/lawn-care"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default LawnCare;
